import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { cvMapSignalStore } from './cv-map-signal-store';
import { CENTER_OF_EUROPE, CvMarkerType, getOptimizedPanoramaView, MapMarkerData, STREET_VIEW_OPTIONS } from '../map-utils';
import { Store } from '@ngrx/store';
import { MapActions } from '../state/map-actions';
import { firstValueFrom } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, take } from 'rxjs/operators';

@Injectable()
export class CvMapService implements OnDestroy {
  private viewService: google.maps.StreetViewService;
  private panorama: google.maps.StreetViewPanorama;
  private googleMap: google.maps.Map;
  private visibleListener: google.maps.MapsEventListener;
  private readonly signalStore = inject(cvMapSignalStore);
  private readonly store = inject(Store);
  private readonly ngZone = inject(NgZone);

  private readonly mapLoading$ = toObservable(this.signalStore.mapLoading);

  /**
   * Initialize street view, set up street view visible listener and update mapsFullyRendered
   * @param panoContainer
   * @param googleMap
   */
  public initStreetView(panoContainer: HTMLElement, googleMap: google.maps.Map) {
    this.googleMap = googleMap;

    this.panorama = new google.maps.StreetViewPanorama(
        panoContainer, STREET_VIEW_OPTIONS(true));
    this.googleMap.setStreetView(this.panorama);

    // Event Listener logic (Pegman / Close button)
    this.visibleListener = this.panorama
        .addListener('visible_changed', () => {
          console.log(`street view visibility changed to visible: ${this.panorama.getVisible()}`);
          // We use ngZone.run so we don't need manual cd.detectChanges()
          this.ngZone.run(() => {
            this.store.dispatch(MapActions.updateStreetViewActive({
              streetViewActive: this.panorama.getVisible(),
            }));

            if (this.panorama.getVisible()) { // street view was enabled by user (dragging pegman)
              this.store.dispatch(MapActions.updateStreetViewClosedByUser({
                streetViewClosedByUser: false,
              }));
            }

            requestAnimationFrame(() => {
              // always trigger a resize event as visibility changes the height of the street view
              // container ... needs to be triggered manually to prevent a 'black screen'
              google.maps.event.trigger(this.panorama, 'resize');
              // The pano markers tend *not* to be shown upon initial load.
              // This is a known issue; monkeypatch-able by setting a zoom first:
              // https://stackoverflow.com/a/49474807/8160318
              this.panorama.setZoom(1);
            });
          });
        });

    // Service and State Initialization
    this.viewService = new google.maps.StreetViewService();
    this.signalStore.updateMapsFullyRendered(true);
  }

  /**
   * Orchestrates Bounds and Street View position
   */
  public async handleMapBoundsAndStreetViewPosition(
      markers: MapMarkerData[],
      streetViewClosedByUser: boolean,
  ) {

    if (!this.googleMap) return;

    const bounds = new google.maps.LatLngBounds();

    // 1. Edge case: No markers -> Center on Europe
    if (!markers || markers.length === 0) {
      bounds.extend(CENTER_OF_EUROPE);
      this.googleMap.fitBounds(bounds);
      this.googleMap.setZoom(4);
      this.panorama?.setVisible(false);
      return;
    }

    // 2. Edge case: Custom Pin (BLS-4521) -> Do not fit bounds after creating custom PIN
    if (markers.some(marker => marker.id === CvMarkerType.CUSTOM)) {
      return;
    }

    // 3. Normal Case: Fit all markers
    markers.forEach(m => bounds.extend(m.position));
    this.googleMap.fitBounds(bounds);

    // Ensure bounds are ready
    if (!this.googleMap.getBounds()) {
      await this.waitUntilMapIsReady();
    }

    // Offset zoom for better overview
    this.googleMap.setZoom(this.googleMap.getZoom() - 2);
    await this.waitUntilMapIsReady();

    // 4. Update Street View
    await this.centerStreetViewPosition(markers, streetViewClosedByUser);
  }


  /**
   * Enable (if disabled) and attempt to center street view on CV markers.
   * @param markers
   * @param streetViewClosedByUser
   * @private
   */
  public async centerStreetViewPosition(
      markers: MapMarkerData[],
      streetViewClosedByUser: boolean,
  ) {
    if (streetViewClosedByUser) {
      this.panorama.setVisible(false);
      return;
    }

    this.signalStore.updateStreetViewLoading(true);

    // BLS-4957: Sort so 'IMPORT' type is the primary focus
    const sortedMarkers = [...markers].sort((a) => (a.id === CvMarkerType.IMPORT ? -1 : 1));
    const primaryMarker = sortedMarkers[0];

    const foundPanoramaCenter = await this.findPanoramaCenter(primaryMarker.position);

    if (!foundPanoramaCenter) {
      this.panorama.setVisible(false);
      console.warn('Unfortunately Street View is not available for these coordinates.');
    } else {
      // Focus on the primary marker, or the map center if markers are somehow empty
      const focalPoint = primaryMarker
          ? new google.maps.LatLng(primaryMarker.position)
          : this.googleMap.getCenter();

      const view = getOptimizedPanoramaView(foundPanoramaCenter, focalPoint);

      this.panorama.setPosition(view.position);
      this.panorama.setPov(view.pov);
      this.panorama.setVisible(true);
    }

    this.signalStore.updateStreetViewLoading(false);
  }

  /**
   * @private
   * Waits for the signal store's mapLoading state to become false.
   * This bridges the reactive signal state back into the async/await flow.
   */
  private async waitUntilMapIsReady(): Promise<void> {
    // 1. If the store already says we are idle, resolve immediately
    if (!this.signalStore.mapLoading()) {
      return Promise.resolve();
    }

    // 2. Otherwise, wait for the state to flip to false
    await firstValueFrom(
        this.mapLoading$.pipe(
            filter(loading => !loading),
            take(1),
        ),
    );
  }

  /**
   * Returns the panorama LatLng for given poi coordinate and given searchRadius (500m by default)
   * @param poi {google.maps.LatLngLiteral}
   * @param searchRadius {number} [searchRadius = 500]
   */
  private async findPanoramaCenter(
      poi: google.maps.LatLngLiteral,
      searchRadius: number = 500,
  ): Promise<google.maps.LatLng | null> {

    console.info(
        `Attempting to find a panorama.` +
        `Looking around point: ${JSON.stringify(poi)}` +
        `w/ radius ${searchRadius}.`,
    );

    // We return the await directly, stripping the extra variable declaration
    return await new Promise<google.maps.LatLng | null>((resolve) => {
      this.viewService.getPanorama(
          {
            location: poi,
            radius: searchRadius,
            preference: google.maps.StreetViewPreference.NEAREST,
            source: google.maps.StreetViewSource.OUTDOOR,
          },
          (data) => {
            if (data) {
              console.info(`Found a panorama!`, { data });
              resolve(data.location.latLng);
            } else {
              // Logged implicitly by the fact that the next step receives null
              resolve(null);
            }
          },
      );
    });
  }

  ngOnDestroy() {
    // Stop the listener (Prevents code from running on a dead component)
    if (this.visibleListener) {
      google.maps.event.removeListener(this.visibleListener);
    }

    // Hide the panorama (Shuts down the internal 3D/WebGL rendering engine)
    if (this.panorama) {
      this.panorama.setVisible(false);
    }

    // Break the link (Tells the 2D map to stop trying to update the 3D view)
    if (this.googleMap) {
      this.googleMap.setStreetView(null);
    }
  }
}